/**
 * DesktopShareButton: 화면 공유 / 문서 공유 / 스케치 모드 드롭다운.
 *
 * v1 원본: RenewalComponents/Buttons/DesktopShareButton/DesktopShareButtonPresenter.js
 * v1 CSS: .rtc_new3 .newHeader_btnItem > .newHeader_btn--dropdown
 * v1 layer: viewerHeader__layer sizeRelase (3-option dropdown)
 *
 * v1 핵심 동작:
 * - 매 렌더링마다 getIsSchechMode/getIsDocumentShare/getIsWhiteboardOwner 호출 (메타데이터 직접 읽기)
 * - isGlobalShare = isScreenShare || (isOpenWhiteboard && isWhiteboardOwner)
 * - 공유 중이면 "공유취소" → handleCloseShare (화면공유 + 화이트보드 동시 종료)
 * - 스케치/문서공유 충돌 시 "이미 스케치모드가 진행중입니다" / "이미 문서공유가 진행중입니다"
 * - 파일 로딩 중 CSS 스피너 + "처리중..."
 */
import React, { useState, useRef, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { toastActions } from '@/store/slices/toastSlice';
import { whiteboardActions } from '@/store/slices/whiteboardSlice';
import {
  startWhiteboardSession,
  stopWhiteboardSession,
  handleFileUploadForDocumentShare,
  startDocumentSharingSession,
  getIsOpenWhiteboardSession,
  getIsWhiteboardOwner,
  getIsDocumentShare,
  getIsSketchMode,
} from '@/utils/whiteboard';
import apiManager from '@/api';
import LUXDialog from '@/components/Dialog';
import WeDriveFinder from '@/components/DocumentShare/WeDriveFinder';

interface DesktopShareButtonProps {
  onToggleScreenShare: () => void;
  onOpenMeetingMaterial?: () => void;
}

const DesktopShareButton: React.FC<DesktopShareButtonProps> = ({ onToggleScreenShare, onOpenMeetingMaterial }) => {
  const isScreenShare = useAppSelector((s) => s.screenShare.isSharing);
  const whiteboard = useAppSelector((s) => s.whiteboard);
  const local = useAppSelector((s) => s.local);
  const isFileLoading = whiteboard.isFileLoading;
  const dispatch = useAppDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [isDocShareOpen, setIsDocShareOpen] = useState(false);
  const [isWeDriveOpen, setIsWeDriveOpen] = useState(false);
  const [, setForceRender] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // v1: 매 렌더링마다 메타데이터에서 직접 상태 확인
  const isOpenWhiteboard = whiteboard.isOpen || getIsOpenWhiteboardSession();
  const isWhiteboardOwner = whiteboard.isOwner || getIsWhiteboardOwner();
  const isDocumentShare = whiteboard.isOpen && !whiteboard.isSketchMode;
  const isSketchMode = whiteboard.isOpen && whiteboard.isSketchMode;

  // v1: isGlobalShare = isScreenShare || (isOpenWhiteboard && isWhiteboardOwner)
  const isGlobalShare = isScreenShare || (isOpenWhiteboard && isWhiteboardOwner);

  // 외부 클릭 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // v1: handleCloseShare — 화면공유 + 화이트보드 동시 종료
  const handleCloseShare = () => {
    if (isScreenShare) {
      onToggleScreenShare();
    }
    if (isOpenWhiteboard && isWhiteboardOwner) {
      stopWhiteboardSession();
    }
    setTimeout(() => {
      setForceRender((n) => n + 1);
    }, 500);
  };

  // v1: 화면 공유 시작
  const handleScreenShare = () => {
    setIsOpen(false);
    onToggleScreenShare();
    setIsDocShareOpen(false);
  };

  // v1: 스케치 모드 시작
  const onClickStartWhiteboard = async () => {
    if (isOpenWhiteboard) {
      LUXDialog.alert(
        isDocumentShare
          ? '이미 문서공유가 진행중입니다.'
          : '이미 스케치모드가 진행중입니다.',
        { type: 'warning', useRenewalStyle: true },
      );
      return;
    }
    setIsOpen(false);
    await startWhiteboardSession();
    setTimeout(() => {
      setForceRender((n) => n + 1);
    }, 500);
  };

  // v1: 문서 공유 2뎁스 메뉴 열기
  const onClickDocShare = () => {
    if (isOpenWhiteboard) {
      LUXDialog.alert(
        isDocumentShare
          ? '이미 문서공유가 진행중입니다.'
          : '이미 스케치모드가 진행중입니다.',
        { type: 'warning', useRenewalStyle: true },
      );
      return;
    }
    setIsDocShareOpen(_=>!_);
  };

  // 파일 선택 (기존 로직)
  const handleFileSelect = () => {
    fileInputRef.current?.click();
    setIsDocShareOpen(false);
  };

  // WE드라이브 열기
  const handleWeDrive = () => {
    setIsWeDriveOpen(true);
    setIsDocShareOpen(false);
    setIsOpen(false)
  };

  // WE드라이브에서 파일 선택 완료
  const handleWeDriveConfirm = async (resources: string[], fileName: string) => {
    setIsWeDriveOpen(false);
    dispatch(whiteboardActions.setIsFileLoading(true));
    try {
      // WE드라이브에서 받은 resources를 이용해 이미지 요소 생성
      const imageDataList: any[] = [];
      const imageElements: any[] = [];

      for (let i = 0; i < resources.length; i++) {
        const fileId = `wedrive-page-${i + 1}-${Date.now()}`;
        const imgSrc = resources[i].replace(
          'http://dev.api0.wehago.com/',
          'https://rtctest.wehago.com/api0/',
        );

        imageDataList.push({
          mimeType: 'image/png',
          id: fileId,
          dataURL: null,
          created: Date.now(),
          thumbnailURL: imgSrc,
        });

        // 이미지 크기 측정
        const img = new Image();
        img.src = imgSrc;
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
        });

        imageElements.push({
          type: 'image',
          id: fileId,
          x: 0,
          y: 0,
          width: img.width || 800,
          height: img.height || 600,
          angle: 0,
          strokeColor: 'transparent',
          backgroundColor: 'transparent',
          fillStyle: 'solid',
          strokeWidth: 1,
          strokeStyle: 'solid',
          roughness: 0,
          opacity: 100,
          fileId,
          locked: true,
          link: null,
          status: 'saved',
          scale: [1, 1],
          groupIds: [],
          roundness: null,
          seed: Math.floor(Math.random() * 1000000),
          version: 1,
          versionNonce: Math.floor(Math.random() * 1000000),
          isDeleted: false,
          boundElements: null,
          updated: Date.now(),
          pageNumber: i + 1,
        });
      }

      // 문서공유 세션 시작
      await startDocumentSharingSession(
        imageElements.length,
        { files: imageDataList, imageElements },
        fileName,
        false,
      );
    } catch (err) {
      console.error('[DesktopShareButton] WE드라이브 문서공유 실패:', err);
      LUXDialog.alert('문서 공유 중 오류가 발생했습니다.', { type: 'error', useRenewalStyle: true });
    } finally {
      dispatch(whiteboardActions.setIsFileLoading(false));
      setTimeout(() => {
        setForceRender((n) => n + 1);
      }, 500);
    }
  };

  // 회의자료 → 설정 다이얼로그 회의자료 탭 열기 (자료 없으면 토스트)
  const handleMeetingMaterials = async () => {
    setIsDocShareOpen(false);
    setIsOpen(false);

    const roomId = local?.roomId;
    if (roomId) {
      try {
        const list = await apiManager.getMaterialList(roomId);
        if (!list || list.length === 0) {
          dispatch(toastActions.showSnackbar({ type: 'info', message: '이 회의에 첨부된 회의자료가 없습니다.' }));
          return;
        }
      } catch {
        dispatch(toastActions.showSnackbar({ type: 'info', message: '이 회의에 첨부된 회의자료가 없습니다.' }));
        return;
      }
    }

    onOpenMeetingMaterial?.();
  };

  // v1: 파일 업로드 처리
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setIsOpen(false);
    if (file) {
      dispatch(whiteboardActions.setIsFileLoading(true));
      try {
        await handleFileUploadForDocumentShare(file);
      } finally {
        dispatch(whiteboardActions.setIsFileLoading(false));
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    setTimeout(() => {
      setForceRender((n) => n + 1);
    }, 500);
  };

  return (
    <>
      <div ref={wrapRef} className={`newHeader_btnItem${isOpen ? ' open' : ''}`}>
        <button
          type="button"
          className="newHeader_btn newHeader_btn--dropdown"
          onClick={() => {
            if(isFileLoading) return;
            if(isGlobalShare) {
              handleCloseShare();
            } else {
              setIsOpen(!isOpen);
              setIsDocShareOpen(false);
            }
          }}
        >
          <span
            className={`newHeader_btnIcon thisLoadings${!isFileLoading ? ' icoShare' : ''}${isGlobalShare ? ' selected icoNoShare' : ''}`.trim()}
            style={
              isFileLoading
                ? {
                    display: 'inline-block',
                    width: 18,
                    height: 18,
                    border: '3px solid rgba(255,255,255,0.3)',
                    borderTop: '3px solid #fff',
                    borderRadius: '50%',
                    animation: 'btnSpin 1s linear infinite',
                    background: 'none',
                  }
                : undefined
            }
          />
          {isFileLoading && (
            <style>{`@keyframes btnSpin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } } .thisLoadings::after { content: none !important; }`}</style>
          )}
          <p className="newHeader_btnText">
            {isFileLoading ? '처리중...' : isGlobalShare ? '공유취소' : '화면공유'}
          </p>
        </button>
        <div className="viewerHeader__layer sizeRelase">
          <ul className="viewerHeader__layerList" style={{overflow:"visible"}}>
            <li>
              <button type="button" className="viewerHeader__layerListBtn" onClick={handleScreenShare}>
                <span className="viewerHeader__btnIcon icoViewShare" />
                화면 공유
              </button>
            </li>
            <li className='viewerHeader__layerList--hasPop'>
              <button type="button" className="viewerHeader__layerListBtn" onClick={onClickDocShare}>
                <span className="viewerHeader__btnIcon icoDocShare2" />
                문서 공유
              </button>
              {isDocShareOpen && (
              <div className="viewerHeader__layerListPopover">
                <ul className="viewerHeader__layerList hasPopover">
                    <li>
                      <span className="viewerHeader__layerListBtn typeBack">문서공유</span>
                  </li>
                  <li>
                      <label htmlFor="fileUpload" onClick={()=>handleFileSelect()} className="viewerHeader__layerListBtn">
                          <span className="viewerHeader__btnIcon icoUploadNew"></span>파일선택
                      </label>
                  </li>
                  <li>
                    <button onClick={()=>handleWeDrive()} type="button" className="viewerHeader__layerListBtn"><span className="viewerHeader__btnIcon icoWedriveNew"></span>WE 드라이브</button>
                    
                </li>
                <li>
                    <button type="button" className="viewerHeader__layerListBtn" onClick={handleMeetingMaterials}><span className="viewerHeader__btnIcon icoMeetDocNew"></span>회의자료</button>
                </li>                                
                </ul>
              </div>
              )}
            </li>
            <li>
              <button type="button" className="viewerHeader__layerListBtn" onClick={onClickStartWhiteboard}>
                <span className="viewerHeader__btnIcon icoSchech" />
                스케치 모드
              </button>
            </li>
          </ul>
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* WE드라이브 파일 선택 모달 */}
      {isWeDriveOpen && (
        <WeDriveFinder
          onClose={() => setIsWeDriveOpen(false)}
          onConfirm={handleWeDriveConfirm}
        />
      )}
    </>
  );
};

export default DesktopShareButton;
